import {
  PRINTER_BODY_ID,
  PRINTER_FONT_FAMILY,
  RESOLVER_CONTENT_CLASSNAME,
} from '@e-socna/printer-contract';
import { socnaColors } from '@e-socna/colors';
import { PrintStyleEnum } from '@e-socna/data-layer';

import { environment } from '../../../environment';

export const PRINTER_SECTION_CLASSNAME = 'socna-printer-section';
export const PRINTER_ORIENTATION_PORTRAIT_CLASSNAME =
  'socna-printer-portrait-orientation';
export const PRINTER_ORIENTATION_LANDSCAPE_CLASSNAME =
  'socna-printer-landscape-orientation';
export const PRINTER_FORMAT_A4_CLASSNAME = 'socna-printer-a4-format';
export const PRINTER_FORMAT_A3_CLASSNAME = 'socna-printer-a3-format';
export const PRINTER_CLASS_SUFFIX = '-style';
// MHO : marks sections printed through the browser's own native print (TinyMCE's
// Ctrl+P / window.print()), where @page CSS size/margin are actually honored.
// The backend PDF pipeline (printer processor, via Puppeteer) explicitly overrides
// paper size and forces zero margins at the protocol level, so it always ignores
// @page CSS entirely - rules scoped to this class must stay out of its way.
export const PRINTER_NATIVE_PRINT_CLASSNAME = 'socna-printer-native-print';

export const A4_HEIGHT_MM = 297;
export const A4_WIDTH_MM = 210;
export const A3_HEIGHT_MM = 420;
export const A3_WIDTH_MM = 297;
export const MARGIN_MM_LEFT = 24;
export const MARGIN_MM_RIGHT = 24;
export const MARGIN_MM_TOP = 24;
export const MARGIN_MM_BOTTOM = 24;
export const A4_LANDSCAPE_PAGE_BACKGROUND_URL =
  `${environment.frontendUrl}/${environment.assetsUriSegment}/editor/a4-landscape-page-background.svg` as const;
export const A4_PORTRAIT_PAGE_BACKGROUND_URL =
  `${environment.frontendUrl}/${environment.assetsUriSegment}/editor/a4-portrait-page-background.svg` as const;
export const A3_LANDSCAPE_PAGE_BACKGROUND_URL =
  `${environment.frontendUrl}/${environment.assetsUriSegment}/editor/a3-landscape-page-background.svg` as const;
export const A3_PORTRAIT_PAGE_BACKGROUND_URL =
  `${environment.frontendUrl}/${environment.assetsUriSegment}/editor/a3-portrait-page-background.svg` as const;

export const PAGE_BREAK_SEPARATOR_CLASSNAME = 'page-break-separator';
export const HIDE_ON_PRINT_CLASSNAME = 'hide-on-print';

export const PAGES_BY_FORMAT_AND_ORIENTATION = [
  {
    formatClassName: PRINTER_FORMAT_A4_CLASSNAME,
    orientationClassName: PRINTER_ORIENTATION_PORTRAIT_CLASSNAME,
    widthMm: A4_WIDTH_MM,
    heightMm: A4_HEIGHT_MM,
    backgroundUrl: A4_PORTRAIT_PAGE_BACKGROUND_URL,
  },
  {
    formatClassName: PRINTER_FORMAT_A4_CLASSNAME,
    orientationClassName: PRINTER_ORIENTATION_LANDSCAPE_CLASSNAME,
    widthMm: A4_HEIGHT_MM,
    heightMm: A4_WIDTH_MM,
    backgroundUrl: A4_LANDSCAPE_PAGE_BACKGROUND_URL,
  },
  {
    formatClassName: PRINTER_FORMAT_A3_CLASSNAME,
    orientationClassName: PRINTER_ORIENTATION_PORTRAIT_CLASSNAME,
    widthMm: A3_WIDTH_MM,
    heightMm: A3_HEIGHT_MM,
    backgroundUrl: A3_PORTRAIT_PAGE_BACKGROUND_URL,
  },
  {
    formatClassName: PRINTER_FORMAT_A3_CLASSNAME,
    orientationClassName: PRINTER_ORIENTATION_LANDSCAPE_CLASSNAME,
    widthMm: A3_HEIGHT_MM,
    heightMm: A3_WIDTH_MM,
    backgroundUrl: A3_LANDSCAPE_PAGE_BACKGROUND_URL,
  },
] as const;

const getPageWidthRules = () =>
  PAGES_BY_FORMAT_AND_ORIENTATION.map(
    ({ formatClassName, orientationClassName, widthMm }) => `
.${PRINTER_SECTION_CLASSNAME}.${formatClassName}.${orientationClassName} {
  max-width: ${widthMm.toFixed(2)}mm;
  width: ${widthMm.toFixed(2)}mm;
  min-width: ${widthMm.toFixed(2)}mm;
}
`,
  ).join('');

export const getSocnaPrinterStyles = () => `
/* Socna Printer */

@media print {
  .${HIDE_ON_PRINT_CLASSNAME}{
    display: none;
  }

  /* TanStack devtools */
  .tsqd-parent-container,
  .tsqd-open-btn,
  .tsqd-main-panel {
    display: none;
  }

  .${PRINTER_SECTION_CLASSNAME} {
    border: none;
    box-shadow: none;
  }

  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    color-adjust: exact;
  }

  /* MHO : TinyMCE normalizes a paragraph containing only &nbsp; (or nothing)
  into <p><br data-mce-bogus="1"></p>. TinyMCE's own oxide skin (content-ui
  CSS) then hides that bogus <br> at print time via "display: contents",
  which silently collapses the paragraph's height - deleting every
  intentional blank line as soon as you print/Ctrl+P. This restores it.
  Higher specificity than the vendor rule (adds the "br" type selector) so
  it wins deterministically, regardless of stylesheet load order. */
  .mce-content-body br[data-mce-bogus]:not([data-mce-bogus="all"]) {
    display: inline !important;
  }
}

.${PRINTER_SECTION_CLASSNAME} {
  background-color: white;
  line-height: 1.4;
  letter-spacing: normal;
  font-family: ${PRINTER_FONT_FAMILY};
}

${getPageWidthRules()}
.${PRINTER_SECTION_CLASSNAME} > :first-child {
  margin-top: 0 !important;
  margin-left: 0 !important;
}

.${PRINTER_SECTION_CLASSNAME} table,
.${PRINTER_SECTION_CLASSNAME} img,
.${PRINTER_SECTION_CLASSNAME} div,
.${PRINTER_SECTION_CLASSNAME} p {
  max-width: 100%;
}

#${PRINTER_BODY_ID} h1,
#${PRINTER_BODY_ID} h2,
#${PRINTER_BODY_ID} h3,
#${PRINTER_BODY_ID} h4,
#${PRINTER_BODY_ID} h5,
#${PRINTER_BODY_ID} h6 {
  break-after: avoid;
}

#${PRINTER_BODY_ID} .${RESOLVER_CONTENT_CLASSNAME} {
  break-inside: avoid;
}

#${PRINTER_BODY_ID}.${PrintStyleEnum.Blank}${PRINTER_CLASS_SUFFIX} h1,
#${PRINTER_BODY_ID}.${PrintStyleEnum.Blank}${PRINTER_CLASS_SUFFIX} h2,
#${PRINTER_BODY_ID}.${PrintStyleEnum.Blank}${PRINTER_CLASS_SUFFIX} h3,
#${PRINTER_BODY_ID}.${PrintStyleEnum.Blank}${PRINTER_CLASS_SUFFIX} h4,
#${PRINTER_BODY_ID}.${PrintStyleEnum.Blank}${PRINTER_CLASS_SUFFIX} h5,
#${PRINTER_BODY_ID}.${PrintStyleEnum.Blank}${PRINTER_CLASS_SUFFIX} h6 {
  color: default;
}

#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaIng}${PRINTER_CLASS_SUFFIX} h1,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaIng}${PRINTER_CLASS_SUFFIX} h2,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaIng}${PRINTER_CLASS_SUFFIX} h3,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaIng}${PRINTER_CLASS_SUFFIX} h4,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaIng}${PRINTER_CLASS_SUFFIX} h5,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaIng}${PRINTER_CLASS_SUFFIX} h6 {
  color: ${socnaColors.socnaIng};
}

#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaReseaux}${PRINTER_CLASS_SUFFIX} h1,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaReseaux}${PRINTER_CLASS_SUFFIX} h2,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaReseaux}${PRINTER_CLASS_SUFFIX} h3,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaReseaux}${PRINTER_CLASS_SUFFIX} h4,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaReseaux}${PRINTER_CLASS_SUFFIX} h5,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaReseaux}${PRINTER_CLASS_SUFFIX} h6 {
  color: ${socnaColors.socnaReseaux};
}

#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaSols}${PRINTER_CLASS_SUFFIX} h1,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaSols}${PRINTER_CLASS_SUFFIX} h2,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaSols}${PRINTER_CLASS_SUFFIX} h3,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaSols}${PRINTER_CLASS_SUFFIX} h4,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaSols}${PRINTER_CLASS_SUFFIX} h5,
#${PRINTER_BODY_ID}.${PrintStyleEnum.SocnaSols}${PRINTER_CLASS_SUFFIX} h6 {
  color: ${socnaColors.socnaSols};
}
`;
